import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, usePlaybooksApi } from '../apis/playbooks-apis';
import { useProductsApi } from '../apis/products-apis';
import { Playbook } from '../types';
import { useToast } from "../components/ui/Toast";
import { useNavigation } from '../contexts/NavigationContext';
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import PlaybookForm, { PlaybookFormRef } from './PlaybookForm';
import { Spinner } from "../components/ui/Spinner";
import { Loader2 } from 'lucide-react';

const LoadingOverlay = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
      <Loader2 className="h-6 w-6 animate-spin text-sky-500" />
      <span className="text-lg font-medium">Saving Playbook... Please wait</span>
    </div>
  </div>
);

export const EditPlaybookPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const initialFetchMade = useRef(false);
  const formRef = useRef<PlaybookFormRef>(null);
  
  const [playbookAgenticgenie, setPlaybook] = useState<Playbook | null>(null);
  const [originalPlaybook, setOriginalPlaybook] = useState<Playbook | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [existingPlaybooks, setExistingPlaybooks] = useState<Playbook[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { 
    showNavigationWarning, 
    pendingNavigation, 
    setShowNavigationWarning, 
    setPendingNavigation 
  } = useNavigation();
  const playbooksApi = usePlaybooksApi();
  const productsApi = useProductsApi();

  const hasUnsavedChanges = !isReadOnly && 
    JSON.stringify(playbookAgenticgenie) !== JSON.stringify(originalPlaybook);

  // Handle browser back button
  const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
    if (hasUnsavedChanges) {
      event.preventDefault();
      event.returnValue = '';
    }
  }, [hasUnsavedChanges]);

  // Set up block for browser back button
  useEffect(() => {
    // Add extra history entry when editing starts
    if (hasUnsavedChanges) {
      window.history.pushState(null, '', window.location.pathname);
    }

    const handlePopState = (event: PopStateEvent) => {
      if (hasUnsavedChanges) {
        // Push state back to prevent navigation
        window.history.pushState(null, '', window.location.pathname);
        // Show warning dialog
        setShowNavigationWarning(true);
        setPendingNavigation('/studio/playbooks');
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, handleBeforeUnload, setShowNavigationWarning, setPendingNavigation]);

  useEffect(() => {
    const fetchData = async () => {
      if (id && !initialFetchMade.current) {
        initialFetchMade.current = true;
        try {
          const getAllPlaybooks = true;
          const [playbookData, allPlaybooks] = await Promise.all([
            playbooksApi.getPlaybook(id),
            playbooksApi.getPlaybooks(getAllPlaybooks)
          ]);
          setPlaybook(playbookData);
          setOriginalPlaybook(playbookData);
          setExistingPlaybooks(allPlaybooks);
        } catch (error: any) {
          console.error('Error fetching data:', error);
          
          if (error instanceof ApiError && error.status === 403) {
            navigate('/studio/unauthorized');
            return;
          }
          
          // For all other errors, show the specific error message or fallback
          const errorMessage = error instanceof ApiError 
            ? error.message 
            : "Failed to fetch playbook details";
            
          addToast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
            duration: 5000
          });
          navigate('/studio/playbooks');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [id, navigate, addToast]);

  const handleSubmit = async () => {
    if (!playbookAgenticgenie) return;

    const isValid = await formRef.current?.validateForm() ?? false;
    if (!isValid) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
        duration: 4000
      });
      return;
    }

    try {
      setIsSaving(true);
      // Check if product name has changed
      const productNameChanged = originalPlaybook?.playbookProductName !== playbookAgenticgenie.playbookProductName;
      
      // Handle product creation/update if needed
      if (productNameChanged && playbookAgenticgenie.playbookProductName) {
        try {
          // This will either find the existing product or create a new one
          await productsApi.findOrCreateProduct(playbookAgenticgenie.playbookProductName);
        } catch (productError) {
          console.error('Error handling product:', productError);
          addToast({
            title: "Warning",
            description: "There was an issue with the product, but the playbook update will proceed",
            variant: "default",
            duration: 4000
          });
          // Continue with playbook update despite product issues
        }
      }
      
      // Update the playbook
      await playbooksApi.updatePlaybook(playbookAgenticgenie);
      const savedPlaybook = { ...playbookAgenticgenie };
      setOriginalPlaybook(savedPlaybook);
      setPlaybook(savedPlaybook);
      
      // Navigate back to playbooks list after successful save
      navigate('/studio/playbooks');
      
      addToast({
        title: "Success",
        description: "Playbook " + playbookAgenticgenie.playbookName + " has been successfully updated",
        variant: "default",
        duration: 4000
      });

      if (pendingNavigation) {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      }
    } catch (error) {
      console.error('Error updating playbook:', error);
      addToast({
        title: "Error",
        description: "Failed to update playbook: " + String(error),
        variant: "destructive",
        duration: 8000
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation('/studio/playbooks');
    } else {
      navigate('/studio/playbooks');
    }
  };

  const handleConfirmNavigation = () => {
    setShowNavigationWarning(false);
    
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    } else {
      navigate('/studio/playbooks');
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  };

  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation('/studio/playbooks');
    } else {
      navigate('/studio/playbooks');
    }
  };

  if (loading || !playbookAgenticgenie) {
    return (
      <div className="flex flex-col items-center my-8 gap-2">
        <Spinner className="border-sky-500" />
        <p className="text-center text-muted-foreground">Loading Playbook...</p>
      </div>
    );
  }

  return (
    <>
      {isSaving && <LoadingOverlay />}
      
      <PlaybookForm 
        ref={formRef}
        playbook={playbookAgenticgenie}
        originalPlaybook={originalPlaybook}
        onPlaybookChange={setPlaybook}
        onSubmit={handleSubmit}
        onCancel={handleNavigateBack}
        isEditing={true}
        readOnly={false}
        onDiscardChanges={handleDiscardChanges}
        hasUnsavedChanges={hasUnsavedChanges}
        setIsReadOnly={setIsReadOnly}
        existingPlaybooks={existingPlaybooks}
        isSaving={isSaving}
      />

      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </>
  );
};

export default EditPlaybookPage;